import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { clearAll } from "@/lib/offline";
import { useInstall } from "@/components/InstallBanner";
import { shareAppOnWhatsApp } from "@/lib/shareApp";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, LogOut, Download, KeyRound, Trash2, Loader2, User, Crown, Wallet, Gift, Share2 } from "lucide-react";

export default function Account() {
  const { tailleur, updateTailleur, logout } = useAuth();
  const navigate = useNavigate();
  const [nom, setNom] = useState(tailleur?.nom || "");
  const [atelier, setAtelier] = useState(tailleur?.atelier || "");
  const [ville, setVille] = useState(tailleur?.ville || "");
  const [waveLink, setWaveLink] = useState(tailleur?.wave_link || "");
  const [waveNumber, setWaveNumber] = useState(tailleur?.wave_number || "");
  const [omNumber, setOmNumber] = useState(tailleur?.om_number || "");
  const [saving, setSaving] = useState(false);
  const [savingPay, setSavingPay] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [delStep, setDelStep] = useState(0);
  const { available: canInstall, promptInstall, IosGuide } = useInstall();

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/account", { nom, atelier, ville });
      updateTailleur(data);
      toast.success("Profil mis à jour");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const savePayment = async () => {
    setSavingPay(true);
    try {
      const { data } = await api.put("/account", { wave_link: waveLink, wave_number: waveNumber, om_number: omNumber });
      updateTailleur(data);
      toast.success("Paiements enregistrés");
    } catch (e) { toast.error(apiError(e)); } finally { setSavingPay(false); }
  };

  const exportData = async () => {
    try {
      const { data } = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `tailleur-pro-export-${Date.now()}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Données exportées");
    } catch { toast.error("Export impossible (hors ligne ?)"); }
  };

  const doLogout = async () => { await clearAll(); logout(); navigate("/"); };

  const deleteAccount = async () => {
    try { await api.delete("/account"); await clearAll(); logout(); toast.success("Compte supprimé"); navigate("/"); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="min-h-screen bg-tp-bg pb-10">
      <div className="bg-tp-green text-white px-4 pt-5 pb-6 flex items-center gap-3">
        <button data-testid="account-back-btn" onClick={() => navigate("/")} className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center"><ArrowLeft size={20} /></button>
        <h1 className="font-head font-bold text-xl">Mon compte</h1>
      </div>

      <div className="px-5 -mt-3">
        <div className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-tp-green/10 flex items-center justify-center"><User size={24} className="text-tp-green" /></div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-tp-ink truncate">{tailleur?.nom}</p>
            <p className="text-sm text-tp-muted">{tailleur?.telephone}</p>
          </div>
          <span className={`text-xs font-bold rounded-full px-2.5 py-1 ${tailleur?.plan === "pro" ? "bg-tp-gold/20 text-tp-gold" : "bg-gray-100 text-tp-muted"}`}>
            {tailleur?.plan === "pro" ? "PRO" : "Gratuit"}
          </span>
        </div>
      </div>

      <div className="px-5 mt-5 space-y-4">
        <p className="font-head font-bold text-tp-ink">Informations de l'atelier</p>
        <AField label="Nom complet" value={nom} onChange={setNom} testId="acc-nom" />
        <AField label="Nom de l'atelier" value={atelier} onChange={setAtelier} testId="acc-atelier" />
        <AField label="Ville" value={ville} onChange={setVille} testId="acc-ville" />
        <Button data-testid="save-account-btn" onClick={save} disabled={saving} className="w-full h-[52px] rounded-full bg-tp-green hover:bg-tp-greenDark font-bold">
          {saving ? <Loader2 className="animate-spin" size={20} /> : "Enregistrer"}
        </Button>
      </div>

      <div className="px-5 mt-6 space-y-4">
        <div>
          <p className="font-head font-bold text-tp-ink flex items-center gap-2"><Wallet size={18} className="text-tp-gold" /> Paiement mobile</p>
          <p className="text-sm text-tp-muted mt-1">Vos coordonnées pour être payé par vos clients (Wave & Orange Money).</p>
        </div>
        <AField label="Lien de paiement Wave" value={waveLink} onChange={setWaveLink} testId="acc-wave-link" placeholder="https://pay.wave.com/..." />
        <p className="text-xs text-tp-muted -mt-2">Générez-le dans l'app Wave Business → « Recevoir de l'argent » → « Créer un lien ».</p>
        <AField label="Numéro Wave" value={waveNumber} onChange={setWaveNumber} testId="acc-wave-number" placeholder="+221 77 000 00 00" />
        <AField label="Numéro Orange Money" value={omNumber} onChange={setOmNumber} testId="acc-om-number" placeholder="+221 77 000 00 00" />
        <Button data-testid="save-payment-btn" onClick={savePayment} disabled={savingPay} className="w-full h-[52px] rounded-full bg-tp-gold hover:bg-tp-gold/90 text-tp-ink font-bold">
          {savingPay ? <Loader2 className="animate-spin" size={20} /> : "Enregistrer mes paiements"}
        </Button>
      </div>

      <div className="px-5 mt-6 space-y-2.5">
        <Row testId="acc-referral" icon={Gift} label="Parrainer & gagner des clients" onClick={() => navigate("/parrainage")} gold />
        <Row testId="acc-share-app" icon={Share2} label="Partager l'application" onClick={() => shareAppOnWhatsApp(tailleur)} />
        {canInstall && <Row testId="acc-install" icon={Download} label="Installer l'application" onClick={promptInstall} />}
        {tailleur?.plan === "gratuit" && (
          <Row testId="acc-pro" icon={Crown} label="Passer au plan Pro" onClick={() => navigate("/pro")} gold />
        )}
        <Row testId="acc-change-pin" icon={KeyRound} label="Changer mon code PIN" onClick={() => setPinOpen(true)} />
        <Row testId="acc-export" icon={Download} label="Exporter mes données (JSON)" onClick={exportData} />
        <Row testId="acc-logout" icon={LogOut} label="Se déconnecter" onClick={doLogout} />
        <Row testId="acc-delete" icon={Trash2} label="Supprimer mon compte" onClick={() => setDelStep(1)} danger />
      </div>

      <ChangePinDialog open={pinOpen} onOpenChange={setPinOpen} />
      {IosGuide}

      <AlertDialog open={delStep > 0} onOpenChange={(o) => !o && setDelStep(0)}>
        <AlertDialogContent className="rounded-2xl max-w-[340px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-head">{delStep === 1 ? "Supprimer votre compte ?" : "Êtes-vous vraiment sûr ?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {delStep === 1 ? "Tous vos clients, mesures et photos seront supprimés." : "Cette action est définitive et irréversible."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annuler</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-account"
              onClick={(e) => { if (delStep === 1) { e.preventDefault(); setDelStep(2); } else deleteAccount(); }}
              className="rounded-full bg-red-600 hover:bg-red-700"
            >
              {delStep === 1 ? "Continuer" : "Supprimer définitivement"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AField({ label, value, onChange, testId, placeholder }) {
  return (
    <div>
      <label className="font-semibold text-tp-ink block mb-1.5 text-sm">{label}</label>
      <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full h-12 rounded-xl bg-white border border-gray-300 px-4 text-base outline-none focus:border-tp-green" />
    </div>
  );
}

function Row({ icon: Icon, label, onClick, danger, gold, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className={`w-full bg-white rounded-xl p-4 border flex items-center gap-3 text-left font-semibold active:scale-[0.99] transition-transform ${
        danger ? "border-red-100 text-red-600" : gold ? "border-tp-gold/40 text-tp-ink" : "border-gray-100 text-tp-ink"}`}>
      <Icon size={20} className={danger ? "text-red-500" : gold ? "text-tp-gold" : "text-tp-green"} />
      {label}
    </button>
  );
}

function ChangePinDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (current.length !== 4 || next.length !== 4) return toast.error("Entrez 4 chiffres");
    setSaving(true);
    try {
      await api.put("/account/pin", { current_pin: current, new_pin: next });
      toast.success("Code PIN changé");
      onOpenChange(false); setCurrent(""); setNext("");
    } catch (e) { toast.error(apiError(e)); setCurrent(""); } finally { setSaving(false); }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-2xl max-w-[360px]">
        <AlertDialogHeader><AlertDialogTitle className="font-head">Changer le code PIN</AlertDialogTitle></AlertDialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-sm font-semibold text-tp-ink mb-2">Code actuel</p>
            <InputOTP maxLength={4} value={current} onChange={setCurrent} inputMode="numeric" data-testid="current-pin">
              <InputOTPGroup className="gap-2">{[0,1,2,3].map((i) => <InputOTPSlot key={i} index={i} className="w-12 h-14 text-2xl rounded-xl border-2 bg-white" />)}</InputOTPGroup>
            </InputOTP>
          </div>
          <div>
            <p className="text-sm font-semibold text-tp-ink mb-2">Nouveau code</p>
            <InputOTP maxLength={4} value={next} onChange={setNext} inputMode="numeric" data-testid="new-pin">
              <InputOTPGroup className="gap-2">{[0,1,2,3].map((i) => <InputOTPSlot key={i} index={i} className="w-12 h-14 text-2xl rounded-xl border-2 bg-white" />)}</InputOTPGroup>
            </InputOTP>
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full">Annuler</AlertDialogCancel>
          <Button data-testid="submit-change-pin" onClick={submit} disabled={saving} className="rounded-full bg-tp-green hover:bg-tp-greenDark">
            {saving ? <Loader2 className="animate-spin" size={18} /> : "Valider"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
